/**
 * LinkButtonContextMenu
 *
 * Context menu that appears on right-click of the link button.
 * Provides options for viewing, creating, and bulk-creating relationships.
 */

import { useEffect, useRef, type ReactElement } from 'react'
import { useTranslation } from 'react-i18next'
import {
  BulkCreateButton,
  CopyLinksButton,
  CreateLinkButton,
  MenuDivider,
  ViewAllButton,
} from './link-button-context-menu/link-button-context-menu.parts'

const MENU_WIDTH_PX = 220
const MENU_HEIGHT_PX = 200

interface LinkButtonContextMenuProps {
  x: number
  y: number
  hasSelection: boolean
  selectionCount: number
  relationshipCount: number
  onClose: () => void
  onViewAllLinks: () => void
  onCreateLink: () => void
  onBulkCreateLink: () => void
  onCopyLinksFrom: () => void
}

function useDismiss(menuRef: React.RefObject<HTMLDivElement | null>, onClose: () => void): void {
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent): void => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) onClose()
    }
    const handleEscape = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('mousedown', handleClickOutside)
    document.addEventListener('keydown', handleEscape)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('keydown', handleEscape)
    }
  }, [menuRef, onClose])
}

export function LinkButtonContextMenu({
  x,
  y,
  hasSelection,
  selectionCount,
  relationshipCount,
  onClose,
  onViewAllLinks,
  onCreateLink,
  onBulkCreateLink,
  onCopyLinksFrom,
}: LinkButtonContextMenuProps): ReactElement {
  const { t } = useTranslation('cases')
  const menuRef = useRef<HTMLDivElement>(null)
  useDismiss(menuRef, onClose)

  const adjustedX = Math.min(x, window.innerWidth - MENU_WIDTH_PX)
  const adjustedY = Math.min(y, window.innerHeight - MENU_HEIGHT_PX)

  const wrap = (fn: () => void) => (): void => {
    fn()
    onClose()
  }

  return (
    <div
      ref={menuRef}
      className="fixed z-50 w-52 rounded-lg border border-slate-200 bg-white py-1 shadow-lg dark:border-slate-700 dark:bg-slate-800"
      style={{ left: adjustedX, top: adjustedY }}
    >
      <ViewAllButton
        onActivate={wrap(onViewAllLinks)}
        relationshipCount={relationshipCount}
        t={t}
      />
      <MenuDivider />
      <CreateLinkButton onActivate={wrap(onCreateLink)} t={t} />
      <BulkCreateButton
        onActivate={wrap(onBulkCreateLink)}
        hasSelection={hasSelection}
        selectionCount={selectionCount}
        t={t}
      />
      <MenuDivider />
      <CopyLinksButton onActivate={wrap(onCopyLinksFrom)} t={t} />
    </div>
  )
}
